"use client";

import { ArrowUpRight, Brain, Cpu, MousePointer2, Network } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const labs = [{
  id: "DIAG-01",
  name: "Class-Level Diagnosis",
  description: "Surfaces exactly which methods and steps the whole class is losing marks on, so you know what to reteach and what the class should practice next.",
  accentVar: "--chart-1",
  image: "https://resseti.com/wp-content/uploads/2026/09/05-feature-class-topics.webp?v=5frxw51ug3m",
  srcset: "https://resseti.com/wp-content/uploads/2026/09/05-feature-class-topics.webp?v=z4u689jv6f-wvc-srcset",
  sizes: "https://resseti.com/wp-content/uploads/2026/09/05-feature-class-topics.webp?v=7rv2f5czl1t-wvc-sizes",
  icon: Brain
}, {
  id: "DIAG-02",
  name: "Individual Student Feedback",
  description: "Every student gets a personalised breakdown of what's costing them marks and what to work on next, generated automatically rather than written by hand.",
  accentVar: "--chart-3",
  image: "https://resseti.com/wp-content/uploads/2026/09/05-feature-student-profile.webp?v=wrtkljwi5p8",
  srcset: "https://resseti.com/wp-content/uploads/2026/09/05-feature-teacher-priority.webp?v=9ztaiwgigg-wvc-srcset",
  sizes: "https://resseti.com/wp-content/uploads/2026/09/05-feature-teacher-priority.webp?v=ctrynu3yui7-wvc-sizes",
  icon: MousePointer2
}, {
  id: "DIAG-03",
  name: "No Manual Marking Analysis",
  description: "Teachers get the class view and every student's feedback without reading through and diagnosing each script by hand.",
  accentVar: "--chart-2",
  image: "https://resseti.com/wp-content/uploads/2026/09/04-tilt-teacher.webp?v=efbmw5h54re",
  srcset: "https://resseti.com/wp-content/uploads/2026/09/04-tilt-teacher.webp?v=4n5jfivyaoe-wvc-srcset",
  sizes: "https://resseti.com/wp-content/uploads/2026/09/04-tilt-teacher.webp?v=fget8b8hw6d-wvc-sizes",
  icon: Cpu
}, {
  id: "DIAG-04",
  name: "Fits Existing Assessments",
  description: "Runs against the homework, classwork, and mocks your students already produce, so there's nothing new to set up.",
  accentVar: "--chart-4",
  image: "https://resseti.com/wp-content/uploads/2026/09/02-browser-diagnosis-1.webp?v=wegxfql57eh",
  srcset: "https://resseti.com/wp-content/uploads/2026/09/02-browser-diagnosis-1.webp?v=gqddpfehh9g-wvc-srcset",
  sizes: "https://resseti.com/wp-content/uploads/2026/09/02-browser-diagnosis-1.webp?v=omu5pauffpe-wvc-sizes",
  icon: Network
}];
export default function Benefits() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="530" id="institutional-benefits" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-36" style={{
    backgroundColor: "hsl(254 70% 5%)"
  }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3Cfilter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-12">
            <span className="font-mono text-xs sm:text-sm tracking-widest uppercase" style={{
            color: "var(--chart-1)"
          }}>
              Institutional Capabilities
            </span>
          </div>
          <div className="lg:col-span-10 lg:col-start-2">
            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.05] tracking-tight text-foreground">
              Four things teachers get.
              <br />
              <span className="text-muted-foreground">Zero extra grading.</span>
            </h2>
          </div>
        </div>

        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2">
          {labs.map((lab, i) => {
          const Icon = lab.icon;
          return <article key={lab.id} data-index={i} className={`group relative flex flex-col bg-background border border-border/40 transition-all duration-700 ease-out hover:border-transparent md:h-[540px] ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
            "--lab-accent": `var(${lab.accentVar})`
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = `var(${lab.accentVar})`;
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "";
          }}>
                <div className="relative h-64 sm:h-72 overflow-hidden">
                  <div className="absolute inset-0 transition-all duration-700 ease-out group-hover:opacity-100" style={{
                backgroundColor: `var(${lab.accentVar})`,
                opacity: 0.85
              }} />
                  <img src={lab.image} data-wvc-srcset={lab.srcset} data-wvc-sizes={lab.sizes} alt={lab.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover mix-blend-luminosity grayscale opacity-60 transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:opacity-90 group-hover:mix-blend-normal" />
                  <div className="absolute top-4 right-4">
                    <Icon className="h-5 w-5 text-foreground/70 transition-colors duration-500 group-hover:text-foreground" strokeWidth={1.5} />
                  </div>
                </div>

                <div className="relative flex flex-1 flex-col p-6 sm:p-7">
                  <div className="flex items-center justify-end">
                    <span className="font-mono text-xs text-muted-foreground/60">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-default mt-4 text-2xl sm:text-3xl leading-tight tracking-tight text-foreground">
                    {lab.name}
                  </h3>

                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                    {lab.description}
                  </p>

                  <div className="mt-auto overflow-hidden pt-6">
                    <Link to="/SeturaPage" className="font-mono text-xs tracking-widest uppercase flex items-center gap-2 translate-y-8 opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100" style={{
                  color: `var(${lab.accentVar})`
                }}>
                      <span>Explore Setura</span>
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>

                <span aria-hidden="true" className="pointer-events-none absolute top-0 left-0 h-3 w-3 border-t border-l opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
              borderColor: `var(${lab.accentVar})`
            }} />
                <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b border-r opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
              borderColor: `var(${lab.accentVar})`
            }} />
              </article>;
        })}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild variant="ghost" className="font-mono text-xs sm:text-sm tracking-widest uppercase text-foreground hover:bg-transparent hover:text-foreground group/btn px-0">
            <Link to="/WhyRessetiPage" className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-border transition-all duration-500 group-hover/btn:w-16" style={{
              backgroundColor: "var(--chart-1)"
            }} />
              <span>DISCOVER THE RESSETI METHOD</span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}